import type {
  RollEntry,
  RollModifier,
  RollOutcome,
  RollReason,
} from '@rollcall/contracts'
import type { TFunction } from 'i18next'
import { useT } from './i18n-context.js'
import { abilityOfPath, contentName, traitLabel, valueLabel } from './labels.js'
import { describeSource } from './provenance.js'

/**
 * Журнал бросков (D12).
 *
 * Показывает то, ради чего он и заводился: что кидали, что выпало
 * на каждой кости, откуда взялись прибавки и какой вышел итог.
 * Отброшенные кости остаются видны в скобках — их бросали, и человек
 * хочет это видеть.
 *
 * Ничего здесь не считается: сумму и отбор костей прислал сервер
 * (D3). Ручной ввод помечен — не чтобы ловить жуликов, а чтобы
 * за столом не спорили, чей бросок настоящий.
 */

/**
 * Подпись источника прибавки — та же, что в разборе величины листа:
 * «Сила +3», «Бонус мастерства +2». Одно место на оба экрана, иначе
 * один и тот же источник назывался бы в них по-разному.
 */
function modifierLabel(t: TFunction, modifier: RollModifier): string {
  const value =
    modifier.value >= 0 ? `+${modifier.value}` : `−${Math.abs(modifier.value)}`
  if (modifier.source.kind === 'notation') {
    return `${t('roll.source.notation')} ${value}`
  }
  return `${describeSource(modifier.source, {}, t)} ${value}`
}

/**
 * По чему кидали — одним именем, без вида величины.
 *
 * Именно именем: «Уход за животными», а не «Навык: Уход
 * за животными». Вид уже сказан причиной броска («Проверка»),
 * и повторять его — это второе двоеточие в подписи. С третьим
 * («Бросить ещё раз: Проверка: Навык: Уход за животными») кнопка
 * и перестала читаться.
 */
function subjectName(t: TFunction, subject: string): string {
  if (subject.startsWith('srd:')) return contentName(t, subject)

  // Характеристика: «Ловкость». Работает и для `check.dex`,
  // и для `saves.dex` — вид опять же скажет причина.
  const ability = abilityOfPath(subject)
  if (ability !== null) return t(`abilities.${ability}`)

  const [head, ...rest] = subject.split('.')
  const tail = rest.join('.')
  // Навык, оружие, заклинание — запись контента: у неё есть имя.
  if (head === 'check' || head === 'attack' || head === 'damage') {
    return contentName(t, tail)
  }
  // Всё остальное — величина листа: там подпись уже без вида
  // («Класс доспеха», «Инициатива»).
  return valueLabel(t, subject) ?? contentName(t, tail === '' ? subject : tail)
}

/** Зачем кидали: «Атака: Кинжал», «Проверка: Скрытность». */
export function reasonLabel(t: TFunction, reason: RollReason): string {
  if (reason.subject === null) {
    return t(`roll.reason.${reason.kind}`, { subject: '' }).trim()
  }
  return t(`roll.reason.${reason.kind}`, { subject: subjectName(t, reason.subject) })
}

/**
 * Критический успех и провал: двадцатка и единица на d20.
 *
 * Только на одиночной кости. С преимуществом или помехой костей две,
 * и выделять там нечего: значение имеет оставленная, а какая из двух
 * осталась — видно и без цвета. Кость с другим числом граней криты
 * не даёт вовсе: двадцатка на d100 — просто двадцатка.
 *
 * Отдельная функция, а не условие в разметке: правило проверяется
 * тестом, а не глазами по трём вкладкам.
 */
export function criticalOf(outcome: RollOutcome): 'success' | 'failure' | null {
  if (outcome.request.sides !== 20) return null
  if (outcome.dice.length !== 1) return null
  const die = outcome.dice[0]
  if (die === undefined || !die.kept) return null
  if (die.value === 20) return 'success'
  if (die.value === 1) return 'failure'
  return null
}

export function RollOutcomeLine({ outcome }: { readonly outcome: RollOutcome }) {
  const t = useT()
  const critical = criticalOf(outcome)
  const dice = outcome.dice
    .map((die) =>
      die.kept ? String(die.value) : t('roll.dropped', { value: die.value }),
    )
    .join(', ')
  const modifiers = outcome.request.modifiers
    .map((modifier) => modifierLabel(t, modifier))
    .join(', ')

  return (
    <>
      <p className="app-roll-total">{t('roll.result', { total: outcome.total })}</p>
      {outcome.dice.length > 0 && (
        <p
          className="app-value-note"
          {...(critical === null ? {} : { 'data-critical': critical })}
        >
          {t('roll.dice', { values: dice })}
          {critical !== null && (
            <>
              {' · '}
              <strong>
                {t(critical === 'success' ? 'roll.critical' : 'roll.fumble')}
              </strong>
            </>
          )}
        </p>
      )}
      {/* Почему костей две: признак листа, а не решение человека.
          Без этого «две двадцатки» в журнале выглядят как случайность
          интерфейса. */}
      {outcome.request.keep?.because != null && (
        <p className="app-value-note">
          {t(
            outcome.request.keep.kind === 'highest'
              ? 'roll.advantage'
              : 'roll.disadvantage',
          )}
          {' · '}
          {t('roll.because', {
            trait: traitLabel(
              t,
              outcome.request.keep.kind === 'highest' ? 'advantages' : 'disadvantages',
              outcome.request.keep.because,
            ),
          })}
        </p>
      )}
      {modifiers !== '' && (
        <p className="app-value-note">{t('roll.modifiers', { parts: modifiers })}</p>
      )}
      {outcome.manual && <p className="app-value-note">{t('roll.manualMark')}</p>}
    </>
  )
}

export function RollLog({ entries }: { readonly entries: readonly RollEntry[] }) {
  const t = useT()

  return (
    <section aria-labelledby="roll-log">
      <h3 id="roll-log">{t('roll.title')}</h3>
      {entries.length === 0 ? (
        <p>{t('roll.empty')}</p>
      ) : (
        <ul className="app-list app-rolls">
          {entries.map((entry) => (
            <li key={entry.id} className="app-roll" data-manual={entry.outcome.manual}>
              <p className="app-value-label">{reasonLabel(t, entry.reason)}</p>
              <RollOutcomeLine outcome={entry.outcome} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
